import React, { useRef, useMemo } from 'react';
import { Player } from '@lordicon/react';
import {
  CategoryMealTag,
  useToast,
  useTranslator,
  cloudflareImage,
  formatCalories,
} from '@front-activerse/common-components';
import { Button, Col } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import { useAppContext } from '@front-activerse/common-components';

interface ICookbookMealItem {
  addToFavorites: (mealId: number) => Promise<any>;
  removeFromFavorites: (mealId: number) => Promise<any>;
  handleRefetch?: () => void;
  orientation: 'horizontal' | 'vertical';
  id: number;
  isFavorite: boolean;
  name: string;
  preparationTime: {
    minutes: number;
  };
  nutrition: {
    singleServing: {
      kcal: number;
    };
  };
  preparationImageUrl: string;
  tags: {
    dietTypeTags?: CategoryMealTag[];
    mealTypeTags?: CategoryMealTag[];
    customTags?: CategoryMealTag[];
  };
  tagsVisibility?: {
    dietTypeTagsVisibleOnHomepage: boolean;
    mealTypeTagsVisibleOnHomepage: boolean;
    customTagsVisibleOnHomepage: boolean;
  };
  rating?: {
    averageRating?: {
      averageRating: number;
    };
  };
  displayRating?: boolean;
}

export const CookbookMealItem: React.FC<ICookbookMealItem> = ({
  id,
  name,
  preparationImageUrl,
  tags,
  tagsVisibility,
  isFavorite,
  addToFavorites,
  removeFromFavorites,
  handleRefetch,
  orientation,
  rating,
  displayRating,
  preparationTime,
  nutrition,
}) => {
  const { t } = useTranslator();
  const { toastError, toastSuccess, dismiss } = useToast();
  const navigate = useNavigate();

  const playerRefBookmark = useRef<Player>(null);
  const iconsIconFgColor = useMemo(() => {
    if (typeof document === 'undefined') return '#000000';
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--icons-icon-fg-color')
      .trim();
    return color || '#000000';
  }, []);

  const iconsIconPrimaryColor = useMemo(() => {
    if (typeof document === 'undefined') return '#000000';
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--icons-icon-primary-color')
      .trim();
    return color || '#000000';
  }, []);
  const {
    consts: { APP_BRAND },
    images: { ratingStarFull, addToTrackerIcon },
    lotties: { bookmarkIcon, bookmarkFullIcon },
  } = useAppContext();

  const { trackFirebaseEvent } = useOutletContext<{
    trackFirebaseEvent: (eventName: string, eventPayload: object) => void;
  }>();

  const toggleFavorite = async () => {
    try {
      if (isFavorite) {
        const response = await removeFromFavorites(id);
        playerRefBookmark.current?.playFromBeginning();
        if (response.data.cookbook.removeFavoriteMeal.code === 200) {
          handleRefetch && (await handleRefetch());
          toastSuccess(
            <p className="mb-0">
              {t('cookbook.favorites.notification.removedFromFav.content')}
              <Button
                color="link"
                onClick={async () => {
                  await addToFavorites(id);
                  handleRefetch && (await handleRefetch());
                  dismiss();
                }}
              >
                {t('meal.favorites.notification.removedFromFav.button')}
              </Button>
            </p>
          );
        }
      } else {
        const response = await addToFavorites(id);
        playerRefBookmark.current?.playFromBeginning();
        if (response.data.cookbook.addFavoriteMeal.successCode === 200) {
          toastSuccess(
            <p className="mb-0">{t('cookbook.favorites.notification.addedToFav.content')}</p>
          );
        }
      }

      trackFirebaseEvent('user_action', {
        action: 'tap-add_favourites',
        location: 'Dietplan',
        meal_id: id,
        cookbook_meal_types: tags.mealTypeTags
          ? tags.mealTypeTags.map((tag: any) => tag.name).join('/')
          : '',
        selected_diet: tags.dietTypeTags
          ? tags.dietTypeTags.map((tag: any) => tag.name).join('/')
          : '',
        meals_tab: 'cookbook',
        meal_favourites: isFavorite,
      });
    } catch (err) {
      toastError();
    }
  };

  let coverImage;

  // image operations
  if (orientation === 'vertical') {
    coverImage = cloudflareImage(preparationImageUrl, `${APP_BRAND}`, 'cookbookMealItemVertical');
  } else {
    coverImage = cloudflareImage(
      preparationImageUrl,
      `${APP_BRAND}`,
      'cookbookMealItemHorizontal2'
    );
  }

  const handleTagClick = (e: React.MouseEvent, tagType: string, tag: CategoryMealTag) => {
    e.stopPropagation();
    const params = new URLSearchParams({ name: tag.name });
    const isOnTagMealsPage = window.location.pathname.includes('/meals-by-tag/');
    navigate(`/meals/cookbook/meals-by-tag/${tagType}/${tag.id}?${params.toString()}`, {
      replace: isOnTagMealsPage,
    });
  };

  const renderTags = () => {
    const tagsArray: any[] = [];
    if (tagsVisibility) {
      if (tagsVisibility.customTagsVisibleOnHomepage) {
        tags.customTags &&
          tags.customTags.map((tag) => {
            tagsArray.push(
              <span
                className="tag me-1 cursor-pointer"
                key={`${tag.__typename}_${tag.id}`}
                style={{ backgroundColor: tag.labelColor, color: '#fff' }}
                onClick={(e) => handleTagClick(e, 'custom', tag)}
              >
                {tag.name}
              </span>
            );
            return null;
          });
      }
      if (tagsVisibility.mealTypeTagsVisibleOnHomepage) {
        tags.mealTypeTags &&
          tags.mealTypeTags.map((tag) => {
            tagsArray.push(
              <span
                className="tag me-1 cursor-pointer"
                key={`${tag.__typename}_${tag.id}`}
                onClick={(e) => handleTagClick(e, 'mealType', tag)}
              >
                {tag.name}
              </span>
            );
            return null;
          });
      }
      if (tagsVisibility.dietTypeTagsVisibleOnHomepage) {
        tags.dietTypeTags &&
          tags.dietTypeTags.map((tag) => {
            tagsArray.push(
              <span
                className="tag me-1 cursor-pointer"
                key={`${tag.__typename}_${tag.id}`}
                onClick={(e) => handleTagClick(e, 'dietType', tag)}
              >
                {tag.name}
              </span>
            );
            return null;
          });
      }
      return tagsArray;
    } else {
      return (
        <>
          {tags.customTags &&
            tags.customTags.map((tag) => (
              <span
                className="tag me-1 cursor-pointer"
                key={`${tag.__typename}_${tag.id}`}
                style={{ backgroundColor: tag.labelColor, color: '#fff' }}
                onClick={(e) => handleTagClick(e, 'custom', tag)}
              >
                {tag.name}
              </span>
            ))}
          {tags.mealTypeTags &&
            tags.mealTypeTags.map((tag) => (
              <span
                className="tag me-1 cursor-pointer"
                key={`${tag.__typename}_${tag.id}`}
                onClick={(e) => handleTagClick(e, 'mealType', tag)}
              >
                {tag.name}
              </span>
            ))}
          {tags.dietTypeTags &&
            tags.dietTypeTags.map((tag) => (
              <span
                className="tag me-1 cursor-pointer"
                key={`${tag.__typename}_${tag.id}`}
                onClick={(e) => handleTagClick(e, 'dietType', tag)}
              >
                {tag.name}
              </span>
            ))}
        </>
      );
    }
  };

  const handleAddMealToTracker = () => {
    navigate(`/meals/cookbook/module/tracker/add-meal/${id}`);
  };

  // vertical orientation
  if (orientation === 'vertical') {
    return (
      <div className="meal">
        <div className="meal-box cursor-pointer">
          <div
            className="meal-image"
            onClick={() => navigate(`/meals/cookbook/meal-details/${id}`)}
          >
            <img src={coverImage} width={612} height={384} className="img-fluid" alt={name} />
            {rating && rating.averageRating && displayRating && (
              <span className="tag tag-rating">
                <img src={ratingStarFull} className="me-1" alt="" />
                {rating.averageRating.averageRating}
              </span>
            )}
          </div>

          <div className="tags mb-2" onClick={() => navigate(`/meals/cookbook/meal-details/${id}`)}>
            {renderTags()}
          </div>

          <div
            role="button"
            className="meal-title mb-s"
            onClick={() => navigate(`/meals/cookbook/meal-details/${id}`)}
          >
            <h4 className="title-medium semi-bold mb-0">{name}</h4>
          </div>

          <div className="d-flex align-items-center justify-content-between">
            <span
              className="description text-text-variant-color"
              onClick={() => navigate(`/meals/cookbook/meal-details/${id}`)}
            >
              {formatCalories(nutrition?.singleServing?.kcal)}&nbsp;kcal
            </span>

            <Button
              color="primary"
              size="sm"
              className="p-xs z-index-999 tracker rounded"
              style={{ minHeight: '32px', lineHeight: '0' }}
              onClick={handleAddMealToTracker}
            >
              <img src={addToTrackerIcon} alt="" />
            </Button>
          </div>
        </div>
        <button
          className="add-remove-fav"
          type="button"
          onClick={toggleFavorite}
          onMouseEnter={() => playerRefBookmark.current?.playFromBeginning()}
          onTouchStart={() => playerRefBookmark.current?.playFromBeginning()}
        >
          <div
            style={{
              width: '32px',
              height: '32px',
              backgroundColor: 'rgba(0, 0, 0, 0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
            }}
          >
            <Player
              ref={playerRefBookmark}
              icon={isFavorite ? bookmarkFullIcon : bookmarkIcon}
              size={22}
              colorize={iconsIconFgColor}
            />
          </div>
        </button>
      </div>
    );
  }

  // horizontal orientation
  return (
    <Col xs="12" md="6" className="meal horizontal">
      <div className="meal-box d-flex cursor-pointer">
        <div className="meal-image" onClick={() => navigate(`/meals/cookbook/meal-details/${id}`)}>
          <img src={coverImage} width={192} height={184} className="img-fluid" alt={name} />
        </div>
        <div className="meal-content">
          <div className="tags me-4" onClick={() => navigate(`/meals/cookbook/meal-details/${id}`)}>
            {rating && rating.averageRating && (
              <span className="tag tag-rating me-1">
                <img src={ratingStarFull} className="me-1" alt="" />
                {rating.averageRating.averageRating}
              </span>
            )}

            {renderTags()}
          </div>
          <div
            role="button"
            className="meal-title mb-s"
            onClick={() => navigate(`/meals/cookbook/meal-details/${id}`)}
          >
            <p className="semi-bold mb-0">{name}</p>
          </div>

          <div className="d-flex align-items-center justify-content-between">
            <span
              className="description text-text-variant-color"
              onClick={() => navigate(`/meals/cookbook/meal-details/${id}`)}
            >
              {formatCalories(nutrition?.singleServing?.kcal)}&nbsp;kcal
            </span>

            <Button
              color="primary"
              size="sm"
              className="p-xs z-index-999 tracker rounded"
              style={{ minHeight: '32px', lineHeight: '0' }}
              onClick={handleAddMealToTracker}
            >
              <img src={addToTrackerIcon} alt="" />
            </Button>
          </div>
        </div>

        <button
          className="add-remove-fav"
          type="button"
          onClick={toggleFavorite}
          onMouseEnter={() => playerRefBookmark.current?.playFromBeginning()}
          onTouchStart={() => playerRefBookmark.current?.playFromBeginning()}
        >
          <Player
            ref={playerRefBookmark}
            icon={isFavorite ? bookmarkFullIcon : bookmarkIcon}
            size={20}
            colorize={iconsIconPrimaryColor}
          />
        </button>
      </div>
    </Col>
  );
};

export default CookbookMealItem;
